import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type {
  Field,
  PricingTable,
  PricingTableSection,
  PricingTableSummaryValues,
  PricingTableSummaryValue,
  BasePricingTableSummaryValue,
  PricingTableSectionSummaryValues,
  TaxBreakdown,
} from '../../types';
import * as PricingTableHelper from './pricing-table.helper';
import * as PricingTableSummaryHelper from './pricing-table-summary.helper';

const DEFAULT_BASE_SUMMARY_VALUE: Required<BasePricingTableSummaryValue> = {
  value: '',
  enabled: false,
  displayName: '',
};

const DEFAULT_SUMMARY_VALUE: Required<PricingTableSummaryValue> = {
  ...DEFAULT_BASE_SUMMARY_VALUE,
  flatFee: false,
};

const DEFAULT_SUMMARY_VALUES: Required<PricingTableSummaryValues> = {
  displayName: '',
  discount: { ...DEFAULT_SUMMARY_VALUE },
  tax: { ...DEFAULT_SUMMARY_VALUE },
  price: { ...DEFAULT_SUMMARY_VALUE },
  baseSubtotal: { ...DEFAULT_BASE_SUMMARY_VALUE },
  taxBreakdown: { ...DEFAULT_BASE_SUMMARY_VALUE, rates: [] },
  combinedSubtotal: { ...DEFAULT_BASE_SUMMARY_VALUE },
};

const DEFAULT_SECTION_SUMMARY_VALUES: Required<PricingTableSectionSummaryValues> = {
  displayName: '',
  discount: { ...DEFAULT_SUMMARY_VALUE },
  tax: { ...DEFAULT_SUMMARY_VALUE },
  price: { ...DEFAULT_SUMMARY_VALUE },
  extendedSummaries: [],
};

const ensureBaseSummaryValue = (
  value?: BasePricingTableSummaryValue
): Required<BasePricingTableSummaryValue> => {
  if (!value) {
    return { ...DEFAULT_BASE_SUMMARY_VALUE };
  }

  return {
    value: value.value ?? DEFAULT_BASE_SUMMARY_VALUE.value,
    enabled: value.enabled ?? DEFAULT_BASE_SUMMARY_VALUE.enabled,
    displayName: value.displayName ?? DEFAULT_BASE_SUMMARY_VALUE.displayName,
  };
};

const ensureSummaryValue = (
  value?: PricingTableSummaryValue
): Required<PricingTableSummaryValue> => {
  if (!value) {
    return { ...DEFAULT_SUMMARY_VALUE };
  }

  return {
    value: value.value ?? DEFAULT_SUMMARY_VALUE.value,
    enabled: value.enabled ?? DEFAULT_SUMMARY_VALUE.enabled,
    flatFee: value.flatFee ?? DEFAULT_SUMMARY_VALUE.flatFee,
    displayName: value.displayName ?? DEFAULT_SUMMARY_VALUE.displayName,
  };
};

const ensureSummaryTaxBreakdown = (value?: TaxBreakdown): Required<TaxBreakdown> => ({
  displayName: value?.displayName ?? t('cpq_tax_breakdown_summary'),
  enabled: value?.enabled ?? DEFAULT_BASE_SUMMARY_VALUE.enabled,
  value: value?.value ?? DEFAULT_BASE_SUMMARY_VALUE.value,
  rates: value?.rates || [],
});

export const ensureSummaryValues = (
  values?: PricingTableSummaryValues
): Required<PricingTableSummaryValues> => {
  if (!values) {
    return { ...DEFAULT_SUMMARY_VALUES };
  }

  return {
    displayName: values.displayName ?? DEFAULT_SUMMARY_VALUES.displayName,
    discount: ensureSummaryValue(values.discount),
    tax: ensureSummaryValue(values.tax),
    price: ensureSummaryValue(values.price),
    combinedSubtotal: {
      ...ensureBaseSummaryValue(values.combinedSubtotal),
      displayName: values?.combinedSubtotal?.displayName ?? t('subtotal'),
    },
    taxBreakdown: ensureSummaryTaxBreakdown(values.taxBreakdown),
    baseSubtotal: {
      ...ensureBaseSummaryValue(values.baseSubtotal),
      displayName: values?.baseSubtotal?.displayName ?? t('cpq_subtotal_before_tax'),
    },
  };
};

export const ensureSectionSummaryValues = (
  values?: PricingTableSectionSummaryValues
): Required<PricingTableSectionSummaryValues> => {
  if (!values) {
    return { ...DEFAULT_SECTION_SUMMARY_VALUES };
  }

  return {
    displayName: values.displayName ?? DEFAULT_SECTION_SUMMARY_VALUES.displayName,
    discount: ensureSummaryValue(values.discount),
    tax: ensureSummaryValue(values.tax),
    price: ensureSummaryValue(values.price),
    extendedSummaries: values.extendedSummaries || [],
  };
};

export const getSectionsWithRowsFromInputValues = (
  node: PricingTable,
  recipientInputFields?: Field[]
): PricingTableSection[] => {
  const nodeFields = node.fields || [];
  const updatedFields = recipientInputFields ? recipientInputFields : nodeFields;
  const sectionsWithEnabledRows = PricingTableHelper.sectionsWithEnabledRows(
    node.pricingTableSections,
    updatedFields
  );
  return PricingTableHelper.sectionsWithRowsFromInputValues(sectionsWithEnabledRows, updatedFields);
};

export const getSummaryValues = (
  node: PricingTable,
  fields: Field[]
): Required<PricingTableSummaryValues> => {
  if (!node) {
    return { ...DEFAULT_SUMMARY_VALUES };
  }

  if (node.preCalculated) {
    return ensureSummaryValues(node.pricingTableSummaryValues);
  }

  const calculatedValues = PricingTableSummaryHelper.calculateSummaryValues(node, fields);
  return ensureSummaryValues(calculatedValues);
};
